import { Component, computed, inject, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { Subject, Observable, of } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MaterialModule } from '../../../../../shared/material.module';
import { TabToolbarComponent } from '../tab-toolbar/tab-toolbar.component';
import { KeywordInputComponent } from '../keyword-input/keyword-input.component';
import { HasUnsavedChanges } from '../../../../../guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { Helpers } from '../../../../../utilities/helpers';
import { CompanySalaryOneService } from '../../../../../services/companies/company-salary-one.service';
import { CompanyDetailsService } from '../../../../../services/companies/company-details.service';
import { LutService } from '../../../../../services/lut.service';
import { CompanyKeywordService } from '../../../../../services/companies/company-keyword.service';
import { CompanyKeywordDto, CompanyKeywordUpdateRequest } from '../../../../../models/api/companyKeywordDto';
import { KeywordType } from '../../../../../models/enums/keywordType.enum';
import { buildKeywordRequests } from '../../../../../utilities/keyword-helpers';
import { saveTabThenKeywords } from '../../../../../utilities/tab-save';
import { serverErrorMessage } from '../../../../../utilities/api-error';
import { Currency } from '../../../../../models/currency';
import { BoardMemberOccupation } from '../../../../../models/boardMemberOccupation';
import { CompensationCategoryConfiguration } from '../../../../../models/compensationCategoryConfiguration';
import {
  SalaryOneResponseDto,
  SalaryOneBoardMemberDto,
  SalaryOneExecutiveMemberDto,
  SalaryOneUpsertRequest,
  SalaryOneBoardMemberUpsertDto,
  SalaryOneExecutiveMemberUpsertDto,
  SalaryOneContext,
} from '../../../../../models/api/companySalaryOneDto';
import { ApiErrorResponse } from '../../../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../../../models/api/errorCodes';
import { selectTabYear, selectDefaultYear } from '../../../../../store/selectors/company-form.selectors';
import { setTabYear } from '../../../../../store/actions/company-form.actions';
import { NumberFormatDirective } from '../../../../../directives/number-format.directive';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { ReadOnlyTabDirective } from '../../../../../directives/read-only-tab.directive';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';

@Component({
  selector: 'app-salary1-tab',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    NgxSpinnerModule,
    TabToolbarComponent,
    KeywordInputComponent,
    NumberFormatDirective,
    ReadOnlyTabDirective,
  ],
  templateUrl: './salary1-tab.component.html',
  styleUrl: './salary1-tab.component.scss'
})
export class Salary1TabComponent implements OnInit, OnDestroy, OnChanges, HasUnsavedChanges {
  @Input() keywords: CompanyKeywordDto[] = [];
  @Output() keywordsUpdated = new EventEmitter<CompanyKeywordDto[]>();

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private salaryOneService = inject(CompanySalaryOneService);
  private companyKeywordService = inject(CompanyKeywordService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);
  private lutService = inject(LutService);
  private companyDetailsService = inject(CompanyDetailsService);
  private currentUserState = inject(CurrentUserState);

  /** Tab held read-only: the form is disabled by ReadOnlyTabDirective and row actions are hidden. */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.Salary1));

  currencies: Currency[] = [];
  occupations: BoardMemberOccupation[] = [];
  companyCountryId: string | null = null;
  categoryConfigurations: CompensationCategoryConfiguration[] = [];
  selectedYear: string = this.currentUserState.defaultMeasurementYear();
  otherMeasurement: string = '';
  otherData: SalaryOneResponseDto | null = null;
  companyId: string | null = null;
  showYearWarning = false;
  isCurrencyCarriedForward = false;
  showKeywords = true;
  companyBoardType: number = 0;
  private destroy$ = new Subject<void>();

  boardDataSource = new MatTableDataSource<SalaryOneBoardMemberDto>([]);
  managementDataSource = new MatTableDataSource<SalaryOneBoardMemberDto>([]);
  executiveDataSource = new MatTableDataSource<SalaryOneExecutiveMemberDto>([]);

  @ViewChild('boardSort') boardSort!: MatSort;
  @ViewChild('managementSort') managementSort!: MatSort;
  @ViewChild('executiveSort') executiveSort!: MatSort;

  // Bit-to-column mapping matching CompensationCategoryConfiguration
  private readonly bitToColumn: Record<number, string> = {
    0: 'salaryOrFees',
    1: 'committeeMeetingFees',
    2: 'bonus',
    3: 'taxableBenefits',
    4: 'stockAwards',
    5: 'optionAwards',
    6: 'nonEquity',
    7: 'longTerm',
    8: 'pensionValue',
    9: 'pensionChange',
    10: 'superannuationPensionBenefits',
    11: 'retirementBenefit',
    12: 'terminationBenefits',
    13: 'longServiceLeave',
    14: 'allOther'
  };

  private readonly allBoardColumns: string[] = [
    'index', 'name', 'currency', 'classification', 'occupation',
    'salaryOrFees', 'committeeMeetingFees', 'bonus', 'taxableBenefits',
    'stockAwards', 'optionAwards', 'nonEquity', 'longTerm',
    'pensionValue', 'pensionChange', 'superannuationPensionBenefits',
    'retirementBenefit', 'terminationBenefits', 'longServiceLeave',
    'allOther', 'totalCompensation'
  ];

  private readonly allExecutiveColumns: string[] = [
    'index', 'name', 'currency', 'occupation',
    'salaryOrFees', 'committeeMeetingFees', 'bonus', 'taxableBenefits',
    'stockAwards', 'optionAwards', 'nonEquity', 'longTerm',
    'pensionValue', 'pensionChange', 'superannuationPensionBenefits',
    'retirementBenefit', 'terminationBenefits', 'longServiceLeave',
    'allOther', 'totalCompensation'
  ];

  boardDisplayedColumns: string[] = [...this.allBoardColumns];
  executiveDisplayedColumns: string[] = [...this.allExecutiveColumns];

  form: FormGroup = this.fb.group({
    boardRows: this.fb.array([]),
    managementRows: this.fb.array([]),
    executiveRows: this.fb.array([]),
    executiveCompensation: [null as number | null],
    directorsCompensation: [null as number | null],
    comments: [''],
    keywords: this.fb.group({
      executiveCompensationOne: [''],
      executiveCompensationTwo: [''],
      directorsCompensationOne: [''],
      directorsCompensationTwo: ['']
    })
  });

  get boardRowsArray(): FormArray { return this.form.get('boardRows') as FormArray; }
  get managementRowsArray(): FormArray { return this.form.get('managementRows') as FormArray; }
  get executiveRowsArray(): FormArray { return this.form.get('executiveRows') as FormArray; }

  get hasChanges(): boolean {
    return this.form.dirty;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  classificationMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Executive',
    2: 'Non-Executive',
    3: 'Independent'
  };

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? null;

    const storedYear = this.store.selectSignal(selectTabYear('salary1'))();
    const defaultYear = this.store.selectSignal(selectDefaultYear)();
    if (storedYear) {
      this.selectedYear = storedYear;
    } else if (defaultYear) {
      this.selectedYear = defaultYear;
      this.store.dispatch(setTabYear({ tabId: 'salary1', year: defaultYear }));
    } else {
      this.store.dispatch(setTabYear({ tabId: 'salary1', year: this.selectedYear }));
    }

    this.patchKeywords();

    this.lutService.getCurrencies().subscribe(data => {
      this.currencies = data.sort((a, b) => a.name.localeCompare(b.name));
    });
    this.lutService.getBoardMemberOccupations().subscribe(data => {
      this.occupations = data.sort((a, b) => a.name.localeCompare(b.name));
    });
    this.lutService.getCompensationCategoryConfigurations().subscribe(data => {
      this.categoryConfigurations = data;
      this.applyColumnFilter();
    });

    if (this.companyId) {
      this.companyDetailsService.get(this.companyId, parseInt(this.selectedYear)).subscribe({
        next: resource => {
          this.companyCountryId = resource.data?.countryId ?? null;
          this.applyColumnFilter();
        },
        error: () => {}
      });
    }

    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['keywords'] && !changes['keywords'].firstChange) {
      this.patchKeywords();
    }
  }

  private patchKeywords(): void {
    const ecOne = this.keywords.find(k => k.type === KeywordType.SalaryOneExecutiveCompensationOne);
    const ecTwo = this.keywords.find(k => k.type === KeywordType.SalaryOneExecutiveCompensationTwo);
    const dcOne = this.keywords.find(k => k.type === KeywordType.SalaryOneDirectorsCompensationOne);
    const dcTwo = this.keywords.find(k => k.type === KeywordType.SalaryOneDirectorsCompensationTwo);
    this.form.get('keywords')!.patchValue({
      executiveCompensationOne: ecOne?.value ?? '',
      executiveCompensationTwo: ecTwo?.value ?? '',
      directorsCompensationOne: dcOne?.value ?? '',
      directorsCompensationTwo: dcTwo?.value ?? ''
    }, { emitEvent: false });
    this.form.markAsPristine();
  }

  private applyColumnFilter(): void {
    if (!this.companyCountryId || this.categoryConfigurations.length === 0) return;

    const config = this.categoryConfigurations.find(c => c.countryId === this.companyCountryId);
    if (!config) return;

    const hiddenColumns = new Set<string>();
    for (const [bit, column] of Object.entries(this.bitToColumn)) {
      if ((config.configuration & (1 << Number(bit))) !== 0) {
        hiddenColumns.add(column);
      }
    }

    this.boardDisplayedColumns = this.allBoardColumns.filter(col => !hiddenColumns.has(col));
    this.executiveDisplayedColumns = this.allExecutiveColumns.filter(col => !hiddenColumns.has(col));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onYearChange(year: string): void {
    if (this.hasChanges) {
      this.confirmationDialog.openConfirmationDialog(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to switch measurement?',
        'Switch', 'Stay', 'swap_horiz', 'edit'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.applyYearChange(year);
        } else {
          const current = this.selectedYear;
          this.selectedYear = '';
          setTimeout(() => this.selectedYear = current);
        }
      });
    } else {
      this.applyYearChange(year);
    }
  }

  private getSaveDescription(): string {
    const hasKeywordChanges = this.form.get('keywords')!.dirty;
    const hasSalaryChanges = this.form.dirty && !hasKeywordChanges ? true :
      this.boardRowsArray.dirty || this.managementRowsArray.dirty || this.executiveRowsArray.dirty ||
      this.form.get('comments')!.dirty;

    if (hasSalaryChanges && hasKeywordChanges) return 'salary data and keywords';
    if (hasKeywordChanges) return 'keywords';
    return 'salary data';
  }

  onSave(): void {
    if (!this.companyId) return;
    this.performSave();
  }

  onOtherMeasurementChange(year: string): void {
    this.otherMeasurement = year;
    if (!year || !this.companyId) {
      this.otherData = null;
      return;
    }
    this.salaryOneService.getSalaryOne(this.companyId, parseInt(year)).subscribe({
      next: resource => {
        this.otherData = resource.state === 'present' ? resource.data : null;
      },
      error: () => { this.otherData = null; }
    });
  }

  getOtherBoardMember(directorId: string | null): SalaryOneBoardMemberDto | undefined {
    if (!directorId) return undefined;
    return this.otherData?.boardMembers?.find(m => m.directorId === directorId);
  }

  getOtherManagementMember(directorId: string | null): SalaryOneBoardMemberDto | undefined {
    if (!directorId) return undefined;
    return this.otherData?.boardMembers?.find(m => m.directorId === directorId);
  }

  getOtherExecutiveMember(directorId: string | null): SalaryOneExecutiveMemberDto | undefined {
    if (!directorId) return undefined;
    return this.otherData?.executiveMembers?.find(m => m.directorId === directorId);
  }

  isDifferent(a: any, b: any): boolean {
    return a !== b && (a != null || b != null);
  }

  formatOther(value: number | null | undefined): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  getClassificationName(classification: number | null): string {
    if (classification == null) return '';
    return this.classificationMap[classification] ?? '';
  }

  getOccupationName(occupationId: string | null): string {
    if (!occupationId) return '';
    return this.occupations.find(o => o.id === occupationId)?.name ?? '';
  }

  getBoardTotal(i: number): number {
    return this.computeRowTotal(this.boardRowsArray.at(i) as FormGroup);
  }

  getManagementTotal(i: number): number {
    return this.computeRowTotal(this.managementRowsArray.at(i) as FormGroup);
  }

  getExecutiveTotal(i: number): number {
    return this.computeRowTotal(this.executiveRowsArray.at(i) as FormGroup);
  }

  toggleRowHighlight(row: any): void {
    row.highlighted = !row.highlighted;
  }

  onBoardSort(sort: Sort): void {
    this.sortTable(sort, this.boardDataSource, this.boardRowsArray, 'boardRows');
  }

  onManagementSort(sort: Sort): void {
    this.sortTable(sort, this.managementDataSource, this.managementRowsArray, 'managementRows');
  }

  onExecutiveSort(sort: Sort): void {
    this.sortTable(sort, this.executiveDataSource, this.executiveRowsArray, 'executiveRows');
  }

  /**
   * The table's current sort, taken from its MatSort header so that rebuilding the
   * rows after a reload (e.g. a save) keeps the column the user picked instead of
   * snapping the data back to name/asc — which left the sort arrow pointing at one
   * column while the rows were ordered by another. Falls back to name/asc before the
   * header has rendered (first load).
   */
  private currentSort(sort: MatSort | undefined): Sort {
    return sort?.active && sort.direction
      ? { active: sort.active, direction: sort.direction }
      : { active: 'name', direction: 'asc' };
  }

  private sortTable(sort: Sort, dataSource: MatTableDataSource<any>, formArray: FormArray, formArrayName: string): void {
    if (!sort.active || sort.direction === '') return;

    const dir = sort.direction === 'asc' ? 1 : -1;

    const paired = dataSource.data.map((row, i) => {
      const group = formArray.at(i) as FormGroup;
      return { merged: { ...row, ...group.value }, group };
    });

    paired.sort((pa, pb) => {
      const a = pa.merged;
      const b = pb.merged;
      let valA: any;
      let valB: any;

      switch (sort.active) {
        case 'name':
          valA = a.directorFullName ?? '';
          valB = b.directorFullName ?? '';
          return dir * valA.localeCompare(valB);
        case 'classification':
          valA = a.classification ?? 99;
          valB = b.classification ?? 99;
          return dir * (valA - valB);
        case 'occupation':
          valA = this.getOccupationName(a.occupationId);
          valB = this.getOccupationName(b.occupationId);
          return dir * valA.localeCompare(valB);
        case 'currency':
          valA = a.currencyId ?? '';
          valB = b.currencyId ?? '';
          return dir * String(valA).localeCompare(String(valB));
        case 'totalCompensation':
          valA = this.computeTotal(a);
          valB = this.computeTotal(b);
          return dir * (valA - valB);
        default:
          valA = a[sort.active] ?? -Infinity;
          valB = b[sort.active] ?? -Infinity;
          return dir * ((Number(valA) || 0) - (Number(valB) || 0));
      }
    });

    // Reuse the existing FormGroup instances so per-row dirty state, values, and
    // valueChanges subscriptions survive the sort. Recreating the groups would reset
    // their dirty flags, and performSave() only sends rows whose group is dirty — so
    // any edit made before a sort would be silently dropped on save.
    dataSource.data = paired.map(p => p.merged);
    this.form.setControl(formArrayName, this.fb.array(paired.map(p => p.group)));
  }

  private readonly totalFields = [
    'salaryOrFees', 'committeeMeetingFees', 'bonus', 'taxableBenefits',
    'stockAwards', 'optionAwards', 'nonEquity', 'longTerm',
    'pensionValue', 'pensionChange', 'superannuationPensionBenefits',
    'retirementBenefit', 'terminationBenefits', 'longServiceLeave', 'allOther'
  ];

  private computeRowTotal(row: FormGroup): number {
    let total = 0;
    for (const field of this.totalFields) {
      const val = row.get(field)?.value;
      if (val != null && val !== '') {
        total += Number(val) || 0;
      }
    }
    return total;
  }

  private computeTotal(obj: any): number {
    let total = 0;
    for (const field of this.totalFields) {
      const val = obj[field];
      if (val != null && val !== '') {
        total += Number(val) || 0;
      }
    }
    return total;
  }

  private applyYearChange(year: string): void {
    this.selectedYear = year;
    this.store.dispatch(setTabYear({ tabId: 'salary1', year }));
    this.loadData();
  }

  private loadData(): void {
    if (!this.companyId) return;

    this.spinnerService.show();
    this.salaryOneService.getSalaryOne(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        const formValues = resource.state === 'present'
          ? resource.data
          : (resource.state === 'carried-forward' ? resource.carriedForward?.values : null);
        this.isCurrencyCarriedForward = resource.state === 'carried-forward';
        this.showYearWarning = this.isCurrencyCarriedForward || resource.state === 'absent';
        if (formValues) {
          this.populateForm(formValues);
        } else if (resource.state === 'absent' && resource.context) {
          const ctx = resource.context as SalaryOneContext;
          this.populateForm({
            comments: null,
            isCurrencyCarriedForward: false,
            boardMembers: ctx.boardMembers ?? [],
            executiveMembers: ctx.executiveMembers ?? []
          });
        } else {
          this.resetForm();
        }
        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();
        this.isCurrencyCarriedForward = false;
        const apiError = error.error as ApiErrorResponse;

        if (apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          this.resetForm();
        } else {
          this.toast.error('Error loading salary data');
        }
        console.error('Salary 1 data error:', error);
      }
    });
  }

  private populateForm(response: SalaryOneResponseDto): void {
    this.form.patchValue({
      comments: response.comments ?? '',
      executiveCompensation: null,
      directorsCompensation: null
    });

    const boardMembers = response.boardMembers ?? [];

    // Detect board type from members
    this.companyBoardType = boardMembers.some(m => m.boardType === 1 || m.boardType === 2) ? 1 : 0;

    if (this.companyBoardType !== 0) {
      const supervisory = boardMembers.filter(m => m.boardType === 1);
      const management = boardMembers.filter(m => m.boardType === 2);
      this.boardDataSource.data = supervisory;
      this.managementDataSource.data = management;
      this.form.setControl('boardRows', this.fb.array(supervisory.map(m => this.createBoardRowGroup(m))));
      this.form.setControl('managementRows', this.fb.array(management.map(m => this.createBoardRowGroup(m))));
    } else {
      this.boardDataSource.data = boardMembers;
      this.managementDataSource.data = [];
      this.form.setControl('boardRows', this.fb.array(boardMembers.map(m => this.createBoardRowGroup(m))));
      this.form.setControl('managementRows', this.fb.array([]));
    }

    const executiveMembers = response.executiveMembers ?? [];
    this.executiveDataSource.data = executiveMembers;
    this.form.setControl('executiveRows', this.fb.array(
      executiveMembers.map(m => this.createExecutiveRowGroup(m))
    ));

    this.onBoardSort(this.currentSort(this.boardSort));
    if (this.companyBoardType !== 0) {
      this.onManagementSort(this.currentSort(this.managementSort));
    }
    this.onExecutiveSort(this.currentSort(this.executiveSort));

    this.form.markAsPristine();
  }

  private createBoardRowGroup(member: SalaryOneBoardMemberDto): FormGroup {
    return this.fb.group({
      boardMemberId: [member.boardMemberId],
      directorId: [member.directorId],
      currencyId: [member.currencyId],
      salaryOrFees: [member.salaryOrFees],
      committeeMeetingFees: [member.committeeMeetingFees],
      bonus: [member.bonus],
      taxableBenefits: [member.taxableBenefits],
      stockAwards: [member.stockAwards],
      optionAwards: [member.optionAwards],
      nonEquity: [member.nonEquity],
      longTerm: [member.longTerm],
      pensionValue: [member.pensionValue],
      pensionChange: [member.pensionChange],
      superannuationPensionBenefits: [member.superannuationPensionBenefits],
      retirementBenefit: [member.retirementBenefit],
      terminationBenefits: [member.terminationBenefits],
      longServiceLeave: [member.longServiceLeave],
      allOther: [member.allOther]
    });
  }

  private createExecutiveRowGroup(member: SalaryOneExecutiveMemberDto): FormGroup {
    return this.fb.group({
      executiveMemberId: [member.executiveMemberId],
      directorId: [member.directorId],
      currencyId: [member.currencyId],
      salaryOrFees: [member.salaryOrFees],
      committeeMeetingFees: [member.committeeMeetingFees],
      bonus: [member.bonus],
      taxableBenefits: [member.taxableBenefits],
      stockAwards: [member.stockAwards],
      optionAwards: [member.optionAwards],
      nonEquity: [member.nonEquity],
      longTerm: [member.longTerm],
      pensionValue: [member.pensionValue],
      pensionChange: [member.pensionChange],
      superannuationPensionBenefits: [member.superannuationPensionBenefits],
      retirementBenefit: [member.retirementBenefit],
      terminationBenefits: [member.terminationBenefits],
      longServiceLeave: [member.longServiceLeave],
      allOther: [member.allOther]
    });
  }

  private resetForm(): void {
    this.boardDataSource.data = [];
    this.managementDataSource.data = [];
    this.executiveDataSource.data = [];
    this.form.setControl('boardRows', this.fb.array([]));
    this.form.setControl('managementRows', this.fb.array([]));
    this.form.setControl('executiveRows', this.fb.array([]));
    this.form.patchValue({ comments: '', executiveCompensation: null, directorsCompensation: null });
    this.form.markAsPristine();
  }

  private performSave(): void {
    if (!this.companyId) return;

    const formVal = this.form.value;

    const boardMembers: SalaryOneBoardMemberUpsertDto[] = [];
    this.boardRowsArray.controls.forEach(group => {
      if (group.dirty || this.isCurrencyCarriedForward) {
        boardMembers.push(this.buildBoardMemberUpsertDto(group.value));
      }
    });
    this.managementRowsArray.controls.forEach(group => {
      if (group.dirty || this.isCurrencyCarriedForward) {
        boardMembers.push(this.buildBoardMemberUpsertDto(group.value));
      }
    });

    const executiveMembers: SalaryOneExecutiveMemberUpsertDto[] = [];
    this.executiveRowsArray.controls.forEach(group => {
      if (group.dirty || this.isCurrencyCarriedForward) {
        executiveMembers.push(this.buildExecutiveMemberUpsertDto(group.value));
      }
    });

    const topLevelDirty = ['comments'].some(f => this.form.get(f)?.dirty);

    // Build keyword upsert requests
    const keywordsValue = this.form.get('keywords')!.value;
    const keywordRequests: CompanyKeywordUpdateRequest[] = buildKeywordRequests(keywordsValue, [
      { formField: 'executiveCompensationOne', type: KeywordType.SalaryOneExecutiveCompensationOne },
      { formField: 'executiveCompensationTwo', type: KeywordType.SalaryOneExecutiveCompensationTwo },
      { formField: 'directorsCompensationOne', type: KeywordType.SalaryOneDirectorsCompensationOne },
      { formField: 'directorsCompensationTwo', type: KeywordType.SalaryOneDirectorsCompensationTwo }
    ], this.keywords);

    const hasSalaryData = boardMembers.length > 0 || executiveMembers.length > 0 || topLevelDirty;
    const hasKeywordData = keywordRequests.length > 0 || this.keywords.some(
      k => k.type === KeywordType.SalaryOneExecutiveCompensationOne ||
           k.type === KeywordType.SalaryOneExecutiveCompensationTwo ||
           k.type === KeywordType.SalaryOneDirectorsCompensationOne ||
           k.type === KeywordType.SalaryOneDirectorsCompensationTwo
    );

    if (!hasSalaryData && !hasKeywordData) {
      this.toast.info('No changes to save');
      return;
    }

    const description = this.getSaveDescription();
    const request: SalaryOneUpsertRequest = {
      year: parseInt(this.selectedYear),
      comments: formVal.comments,
      boardMembers,
      executiveMembers
    };

    this.spinnerService.show();

    const saveCalls: Observable<any>[] = [];
    saveCalls.push(hasSalaryData
      ? this.salaryOneService.updateSalaryOne(this.companyId!, request)
      : of(null));
    saveTabThenKeywords(saveCalls, this.companyKeywordService.upsert(this.companyId!, keywordRequests)).subscribe({
      next: ([_salaryResult, keywordsResult]) => {
        this.keywordsUpdated.emit(keywordsResult as CompanyKeywordDto[]);
        this.form.markAsPristine();
        this.spinnerService.hide();
        this.toast.success(`Saved ${description} successfully`);
        if (hasSalaryData) {
          this.loadData();
        }
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error(serverErrorMessage(err, `Failed to save ${description}`));
        console.error('Save error:', err);
      }
    });
  }

  private buildBoardMemberUpsertDto(value: any): SalaryOneBoardMemberUpsertDto {
    return {
      boardMemberId: value.boardMemberId,
      directorId: value.directorId,
      currencyId: value.currencyId,
      salaryOrFees: value.salaryOrFees,
      committeeMeetingFees: value.committeeMeetingFees,
      bonus: value.bonus,
      taxableBenefits: value.taxableBenefits,
      stockAwards: value.stockAwards,
      optionAwards: value.optionAwards,
      nonEquity: value.nonEquity,
      longTerm: value.longTerm,
      pensionValue: value.pensionValue,
      pensionChange: value.pensionChange,
      superannuationPensionBenefits: value.superannuationPensionBenefits,
      retirementBenefit: value.retirementBenefit,
      terminationBenefits: value.terminationBenefits,
      longServiceLeave: value.longServiceLeave,
      allOther: value.allOther
    };
  }

  private buildExecutiveMemberUpsertDto(value: any): SalaryOneExecutiveMemberUpsertDto {
    return {
      executiveMemberId: value.executiveMemberId,
      directorId: value.directorId,
      currencyId: value.currencyId,
      salaryOrFees: value.salaryOrFees,
      committeeMeetingFees: value.committeeMeetingFees,
      bonus: value.bonus,
      taxableBenefits: value.taxableBenefits,
      stockAwards: value.stockAwards,
      optionAwards: value.optionAwards,
      nonEquity: value.nonEquity,
      longTerm: value.longTerm,
      pensionValue: value.pensionValue,
      pensionChange: value.pensionChange,
      superannuationPensionBenefits: value.superannuationPensionBenefits,
      retirementBenefit: value.retirementBenefit,
      terminationBenefits: value.terminationBenefits,
      longServiceLeave: value.longServiceLeave,
      allOther: value.allOther
    };
  }
}
